import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "use-intl";
import { useAuthStore } from "@/lib/Auth/authStore";
import {
  requestCode,
  verifyCode,
  loginWithPassword,
  RequiresVerificationError,
} from "@/lib/Auth/authApi";
import { Button } from "@/components/catalyst/button";
import { Heading } from "@/components/catalyst/heading";
import { Text } from "@/components/catalyst/text";
import { Link } from "@/components/catalyst/link";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/catalyst/input-otp";
import { Spinner } from "@/lib/Components/Layout/Loaders/Spinner";
import { PencilIcon } from "@heroicons/react/16/solid";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";

type Step = "email" | "code" | "password";

export function LoginScreen() {
  const t = useTranslations("auth.login");
  /**
   * Arriving from sign up: the account exists and its code is already sent.
   * Read loosely - importing the route here would close an import cycle.
   */
  const { email: signedUpEmail } = useSearch({ strict: false }) as {
    email?: string;
  };
  const [step, setStep] = useState<Step>(signedUpEmail ? "code" : "email");
  const [email, setEmail] = useState(signedUpEmail ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval>>(null);
  const verifyingRef = useRef(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setToken } = useAuthStore();

  async function handleAuthSuccess(token: string) {
    setToken(token);
    await queryClient.removeQueries();
    await navigate({ to: "/" });
  }

  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setError(null);
    setIsLoading(true);

    try {
      await requestCode(email);
      setStep("code");
      startCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failedToSendCode"));
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerifyCode(value: string) {
    if (value.length !== 6 || verifyingRef.current) return;

    verifyingRef.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const response = await verifyCode(email, value);
      handleAuthSuccess(response.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("invalidOrExpiredCode"));
      setCode("");
      verifyingRef.current = false;
    } finally {
      setIsLoading(false);
    }
  }

  async function handlePasswordLogin(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim() || !password) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await loginWithPassword(email, password);
      handleAuthSuccess(response.token);
    } catch (err) {
      if (err instanceof RequiresVerificationError) {
        await requestCode(email);
        setStep("code");
        setError(err.message);
        return;
      }

      setError(err instanceof Error ? err.message : t("invalidCredentials"));
    } finally {
      setIsLoading(false);
    }
  }

  function startCooldown() {
    setResendCooldown(30);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  useEffect(() => {
    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, []);

  async function handleResend() {
    setIsResending(true);
    setError(null);

    try {
      await requestCode(email);
      startCooldown();
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failedToSendCode"));
    } finally {
      setIsResending(false);
    }
  }

  return (
    <div className="flex w-full items-center justify-center">
      <div className="w-full max-w-xs space-y-4">
        <div className="text-center">
          <Heading>{t("title")}</Heading>
          {step === "code" ? (
            <div className="mt-1 flex items-center justify-center gap-1">
              <Text>{t("codeSent", { email })}</Text>
              <button
                type="button"
                className="text-zinc-400 hover:text-zinc-600"
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
                title={t("changeEmail")}
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : step === "password" ? (
            <div className="mt-1 flex items-center justify-center gap-1">
              <Text>{email}</Text>
              <button
                type="button"
                className="text-zinc-400 hover:text-zinc-600"
                onClick={() => {
                  setStep("email");
                  setPassword("");
                  setError(null);
                }}
                title={t("changeEmail")}
              >
                <PencilIcon className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            <Text className="mt-1">{t("enterEmail")}</Text>
          )}
        </div>

        {error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </div>
        ) : null}

        {step === "email" ? (
          <form onSubmit={handleRequestCode} className="space-y-3">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                {t("emailLabel")}
              </label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder={t("emailPlaceholder")}
                autoFocus
              />
            </div>
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? t("sending") : t("sendCode")}
            </Button>
            <div className="text-center text-sm text-gray-500">
              {t("noAccount")}{" "}
              <Link to="/sign-up" className="font-medium text-indigo-600">
                {t("signUp")}
              </Link>
            </div>
          </form>
        ) : step === "code" ? (
          <div className="space-y-4">
            <div className="flex items-center justify-center gap-2">
              <InputOTP
                maxLength={6}
                value={code}
                onChange={(value) => {
                  setCode(value);
                  if (value.length === 6) {
                    handleVerifyCode(value);
                  }
                }}
                disabled={isLoading}
                autoFocus
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="rounded-md border border-gray-200 p-2 text-gray-400 hover:border-gray-300 hover:text-gray-600 disabled:opacity-50"
                    onClick={handleResend}
                    disabled={isResending || isLoading || resendCooldown > 0}
                  >
                    {isResending ? (
                      <Spinner className="size-4" />
                    ) : (
                      <svg
                        className="size-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"
                        />
                      </svg>
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {resendCooldown > 0
                    ? `Resend code (${resendCooldown}s)`
                    : t("resendCode")}
                </TooltipContent>
              </Tooltip>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400">
                {t("orUsePassword")}
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <button
              type="button"
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              onClick={() => {
                setError(null);
                setCode("");
                setStep("password");
              }}
            >
              {t("usePassword")}
            </button>
          </div>
        ) : step === "password" ? (
          <div className="space-y-4">
            <form onSubmit={handlePasswordLogin} className="space-y-3">
              <div>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                  placeholder={t("passwordPlaceholder")}
                  autoFocus
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? t("signingIn") : t("signInWithPassword")}
              </Button>
              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-indigo-600"
                >
                  Forgot password?
                </Link>
              </div>
            </form>

            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-xs text-gray-400">{t("orUseCode")}</span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <button
              type="button"
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
              onClick={() => {
                setError(null);
                setPassword("");
                handleResend();
                setStep("code");
              }}
            >
              {t("resendCode")}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
